import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Eye, ShieldCheck, Zap, Users, Heart, CheckCircle, ArrowRight, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const AboutPage = () => {
  const { t } = useLanguage();

  const values = [
    { id: 'transparency', icon: Eye, titlePt: 'Transparência', titleEn: 'Transparency' },
    { id: 'commitment', icon: Heart, titlePt: 'Comprometimento', titleEn: 'Commitment' },
    { id: 'efficiency', icon: Zap, titlePt: 'Eficiência', titleEn: 'Efficiency' },
    { id: 'security', icon: ShieldCheck, titlePt: 'Segurança', titleEn: 'Security' },
    { id: 'client-focus', icon: Users, titlePt: 'Foco no cliente', titleEn: 'Client focus' },
  ];

  const capabilities = [
    { pt: 'Importação por Conta e Ordem de Terceiros', en: 'Import on Behalf of Third Parties' },
    { pt: 'Importação por Encomenda', en: 'Import by Order' },
    { pt: 'Planejamento estratégico da operação', en: 'Strategic operation planning' },
    { pt: 'Escolha e validação de fornecedores internacionais', en: 'International supplier selection and validation' },
    { pt: 'Contratação e gestão da logística internacional', en: 'International logistics management' },
    { pt: 'Classificação fiscal (NCM)', en: 'Tax classification (NCM)' },
    { pt: 'Estruturação tributária e fiscal da operação', en: 'Tax and fiscal structuring' },
    { pt: 'Utilização estratégica de benefícios fiscais (INVEST IMPORT)', en: 'Strategic use of tax benefits (INVEST IMPORT)' },
    { pt: 'Nacionalização e acompanhamento completo da carga', en: 'Complete nationalization and cargo monitoring' },
    { pt: 'Habilitação RADAR e licenças para importar', en: 'RADAR registration and import licenses' },
  ];

  return (
    <div className="min-h-screen pt-24 bg-white">
      {/* HERO — foco específico da página Sobre */}
      <section className="bg-white pt-4 md:pt-8 pb-10 md:pb-14">
        <div className="cv-container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
                {t('QUEM SOMOS', 'ABOUT US')}
              </span>
            </div>
            <h1 className="text-[36px] sm:text-[44px] lg:text-[52px] tracking-tight font-bold text-abyssal leading-[1.05] mb-5">
              {t('Uma trading company com ', 'A trading company with ')}
              <span className="text-cobalt">{t('rigor técnico e proximidade.', 'technical rigor and proximity.')}</span>
            </h1>
            <p className="text-lg text-steel leading-relaxed max-w-2xl">
              {t(
                'Estruturamos operações complexas de importação para empresas brasileiras. Do planejamento tributário à entrega, coordenamos as etapas e antecipamos riscos.',
                'We structure complex import operations for Brazilian companies. From tax planning to delivery, we coordinate every step and anticipate risks.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* MISSÃO / VISÃO — cards maiores */}
      <section className="py-16 md:py-24 bg-mist border-t border-[#E6E6ED]">
        <div className="cv-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-[#E6E6ED] rounded-md p-10 md:p-12"
              data-testid="mission-card"
            >
              <Target className="h-10 w-10 text-cobalt mb-6" strokeWidth={1.5} />
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal mb-3">
                {t('MISSÃO', 'MISSION')}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-abyssal mb-4 leading-tight">
                {t('Estruturar e conduzir operações de importação.', 'Structure and conduct import operations.')}
              </h2>
              <p className="text-base md:text-lg text-steel leading-relaxed">
                {t(
                  'Estruturar e conduzir operações de importação com segurança, eficiência e previsibilidade.',
                  'Structure and conduct import operations with security, efficiency, and predictability.'
                )}
              </p>
            </motion.article>
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 }}
              className="bg-white border border-[#E6E6ED] rounded-md p-10 md:p-12"
              data-testid="vision-card"
            >
              <Eye className="h-10 w-10 text-cobalt mb-6" strokeWidth={1.5} />
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal mb-3">
                {t('VISÃO', 'VISION')}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-abyssal mb-4 leading-tight">
                {t('Excelência técnica e confiança.', 'Technical excellence and trust.')}
              </h2>
              <p className="text-base md:text-lg text-steel leading-relaxed">
                {t(
                  'Ser reconhecida pela excelência técnica e pela confiança na gestão de operações de comércio exterior.',
                  'To be recognized for technical excellence and trust in managing international trade operations.'
                )}
              </p>
            </motion.article>
          </div>
        </div>
      </section>

      {/* VALORES — grid responsivo 2/3/5 */}
      <section className="py-16 md:py-24 bg-white border-t border-[#E6E6ED]">
        <div className="cv-container">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-8 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
                {t('NOSSOS VALORES', 'OUR VALUES')}
              </span>
              <span className="h-px w-8 bg-brass"></span>
            </div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-abyssal">
              {t('O que nos guia', 'What guides us')}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {values.map((v) => (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E6E6ED] p-6 rounded-md text-center"
                data-testid={`value-${v.id}`}
              >
                <v.icon className="h-6 w-6 text-cobalt mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-sm font-semibold text-abyssal">{t(v.titlePt, v.titleEn)}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOSSA ATUAÇÃO — grid 2 colunas desktop, 1 mobile */}
      <section className="py-16 md:py-24 bg-mist border-t border-[#E6E6ED]">
        <div className="cv-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-8 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
                {t('NOSSA ATUAÇÃO', 'OUR EXPERTISE')}
              </span>
              <span className="h-px w-8 bg-brass"></span>
            </div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-abyssal">
              {t('Serviço completo de ponta a ponta', 'Complete end-to-end service')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-5xl mx-auto">
            {capabilities.map((c) => (
              <motion.div
                key={c.en}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-start gap-3 bg-white border border-[#E6E6ED] p-4 rounded-md"
              >
                <CheckCircle className="h-5 w-5 text-cobalt flex-shrink-0 mt-0.5" />
                <span className="text-base text-abyssal leading-relaxed">{t(c.pt, c.en)}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final — GRADIENTE oficial */}
      <section className="cv-cta-gradiente relative">
        <div className="absolute inset-0 bg-abyssal/45" aria-hidden="true"></div>
        <div className="relative cv-container py-20 md:py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white">
                {t('FALE CONOSCO', 'GET IN TOUCH')}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[40px] tracking-tight font-bold text-white mb-5 leading-[1.1]">
              {t('Vamos estruturar sua próxima importação?', "Let's structure your next import?")}
            </h2>
            <p className="text-lg text-white/85 mb-8 leading-relaxed">
              {t(
                'Converse com nossa equipe para avaliar o melhor modelo e conduzir a operação com segurança, eficiência e previsibilidade.',
                'Talk to our team to evaluate the best model and conduct the operation with security, efficiency and predictability.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://wa.me/5527996482098"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 bg-white text-abyssal px-7 py-3.5 rounded-md font-semibold hover:bg-mist transition-colors"
              >
                <Phone className="h-5 w-5" />
                {t('Falar com especialista', 'Talk to specialist')}
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center gap-2 bg-cobalt hover:bg-white hover:text-abyssal text-white border border-white/30 px-7 py-3.5 rounded-md font-semibold transition-colors"
              >
                {t('Enviar mensagem', 'Send message')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
